import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { predefinedIcons } from '../../constants/menuIcons';
import { 
  MdInfoOutline, 
  MdOutlineSync, 
  MdPersonOutline, 
  MdOutlineSettings, 
  MdOutlineCalendarToday, 
  MdOutlineImage, 
  MdMailOutline 
} from 'react-icons/md';
import { FiShield, FiPrinter } from 'react-icons/fi';
import LegalAcceptanceModal from './LegalAcceptanceModal';

const AppDetailsModal = ({ selectedApp, allUsers, setSelectedApp, setSelectedUser, setAlertModalOpen, updateAppStatus }) => {
  //const [isUpdating, setIsUpdating] = useState(false);
  const [localApp, setLocalApp] = useState(selectedApp);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    setLocalApp(selectedApp);
  }, [selectedApp]);

  if (!localApp) return null;

  // Classes condicionais para dark mode
  const modalBg = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const textColor = isDarkMode ? 'text-white' : 'text-gray-900';
  const textColorSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textColorTertiary = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const borderColor = isDarkMode ? 'border-gray-800' : 'border-gray-100';
  const sectionBg = isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50/50';
  const contentBg = isDarkMode ? 'bg-gray-900/50' : 'bg-white';
  const footerBg = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50/80 border-gray-100 backdrop-blur-md';
  //const inputBg = isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900';
  const buttonClose = isDarkMode
    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600'
    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100';

  // const handleStatusUpdate = async (newStatus) => {
  //   setIsUpdating(true);
  //   try {
  //     await updateAppStatus(localApp.id, newStatus);
  //     setLocalApp(prev => ({
  //       ...prev,
  //       status: newStatus
  //     }));
  //   } catch (error) {
  //     console.error('Erro ao atualizar status:', error);
  //     alert('Erro ao atualizar status. Tente novamente.');
  //   } finally {
  //     setIsUpdating(false);
  //   }
  // };

  const translateStatus = (status) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'published': return 'Publicado';
      case 'development': return 'Em Desenvolvimento';
      case 'review': return 'Em Revisão';
      case 'rejected': return 'Rejeitado';
      case 'pending': return 'Pendente';
      default: return status || 'Não definido';
    }
  };

  const getStatusColorClass = (status) => {
    const baseClasses = 'px-3 py-1.5 rounded-full text-xs font-medium border';

    if (isDarkMode) {
      switch (status) {
        case 'active':
        case 'published':
          return `${baseClasses} bg-green-900/50 text-green-300 border-green-700`;
        case 'development':
        case 'review':
          return `${baseClasses} bg-yellow-900/50 text-yellow-300 border-yellow-700`;
        case 'rejected':
          return `${baseClasses} bg-red-900/50 text-red-300 border-red-700`;
        case 'pending':
          return `${baseClasses} bg-blue-900/50 text-blue-300 border-blue-700`;
        default:
          return `${baseClasses} bg-gray-700 text-gray-300 border-gray-600`;
      }
    } else {
      switch (status) {
        case 'active':
        case 'published':
          return `${baseClasses} bg-green-100 text-green-800 border-green-200`;
        case 'development':
        case 'review':
          return `${baseClasses} bg-yellow-100 text-yellow-800 border-yellow-200`;
        case 'rejected':
          return `${baseClasses} bg-red-100 text-red-800 border-red-200`;
        case 'pending':
          return `${baseClasses} bg-blue-100 text-blue-800 border-blue-200`;
        default:
          return `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`;
      }
    }
  };

  const getPlatformBadgeClass = () => {
    return isDarkMode
      ? 'px-2 py-1 bg-blue-900/50 text-blue-300 rounded text-xs font-medium'
      : 'px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium';
  };

  const getIconBgClass = () => {
    return isDarkMode
      ? 'p-2.5 bg-gray-800 shadow-sm rounded-xl border border-gray-700/50'
      : 'p-2.5 bg-white shadow-sm rounded-xl border border-gray-100';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className={`rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col border ${modalBg} overflow-hidden`}>
        {/* Header */}
        <div className={`flex justify-between items-center px-8 py-6 border-b z-10 ${borderColor} ${modalBg}`}>
          <div>
            <h3 className={`text-xl font-semibold ${textColor}`}>Detalhes do Aplicativo</h3>
            <p className={`text-sm mt-1 ${textColorSecondary}`}>Informações completas do aplicativo</p>
          </div>
          <button
            onClick={() => setSelectedApp(null)}
            className={`transition-colors p-2 rounded-md ${buttonClose}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar flex-1">
          {/* Informações Básicas e Status */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Informações Básicas */}
            <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={getIconBgClass()}>
                  <MdInfoOutline className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${textColor}`}>Informações Básicas</h4>
                  <p className={`text-xs ${textColorSecondary}`}>Dados principais do seu aplicativo</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Nome do Aplicativo</label>
                  <p className={`font-semibold p-3.5 rounded-xl border ${contentBg} ${borderColor} ${textColor}`}>
                    {localApp.name || 'Não informado'}
                  </p>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>URL</label>
                  <p className={`font-mono p-3.5 rounded-xl border break-all text-sm font-medium ${contentBg} ${borderColor} ${isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                    {localApp.url}
                  </p>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Valor Total</label>
                  <p className={`text-xl font-bold p-3.5 rounded-xl border ${contentBg} ${borderColor} ${isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>
                    R$ {localApp.totalPrice?.toFixed(2) || '0,00'}
                  </p>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>ID do Aplicativo</label>
                  <p className={`font-mono p-3.5 rounded-xl border break-all text-xs text-gray-500 ${contentBg} ${borderColor}`}>
                    {localApp.id}
                  </p>
                </div>
              </div>
            </div>

            {/* Status e Proprietário */}
            <div className="space-y-8">
              {/* Status */}
              <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={getIconBgClass()}>
                    <MdOutlineSync className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h4 className={`text-lg font-bold ${textColor}`}>Status do Aplicativo</h4>
                    <p className={`text-xs ${textColorSecondary}`}>Fase atual do projeto</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className={getStatusColorClass(localApp.status)}>
                      {translateStatus(localApp.status)}
                    </span>

                    {/* {isUpdating ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-500 border-t-transparent"></div>
                        <span className={`text-xs ${textColorTertiary}`}>Atualizando...</span>
                      </div>
                    ) : (
                      <select
                        value={localApp.status}
                        onChange={(e) => handleStatusUpdate(e.target.value)}
                        className={`flex-1 px-3 py-1.5 text-xs border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${inputBg}`}
                      >
                        <option value="pending">Pendente</option>
                        <option value="development">Em Desenvolvimento</option>
                        <option value="review">Em Revisão</option>
                        <option value="published">Publicado</option>
                        <option value="active">Ativo</option>
                        <option value="rejected">Rejeitado</option>
                      </select>
                    )} */}
                  </div>
                </div>
              </div>

              {/* Proprietário */}
              <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={getIconBgClass()}>
                    <MdPersonOutline className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h4 className={`text-lg font-bold ${textColor}`}>Proprietário</h4>
                    <p className={`text-xs ${textColorSecondary}`}>Dados do cliente</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Email</label>
                    <p className={`font-semibold p-3.5 rounded-xl border break-all text-sm ${contentBg} ${borderColor} ${textColor}`}>
                      {localApp.userEmail || 'Não informado'}
                    </p>
                  </div>

                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>ID do Usuário</label>
                    <p className={`font-mono text-gray-500 p-3.5 rounded-xl border break-all text-xs ${contentBg} ${borderColor}`}>
                      {localApp.userId || 'Não informado'}
                    </p>
                  </div>

                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Tipo de Conta</label>
                    <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                      {allUsers.find(u => u.id === localApp.userId)?.isAdmin ? 'Administrador' : 'Usuário Padrão'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Validade Jurídica & Aceite Contratual */}
          <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-xl">
                  <FiShield className="w-5 h-5" />
                </div>
                <div>
                  <h4 className={`text-base sm:text-lg font-bold ${textColor}`}>Validade Jurídica & Aceite do Contrato</h4>
                  <p className={`text-xs ${textColorSecondary}`}>Assinatura eletrônica e comprovação para cobrança/rescisão</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setLegalModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-bold shadow-md hover:shadow-lg transition-all active:scale-95"
              >
                <FiPrinter className="w-4 h-4" />
                <span>Imprimir Comprovante de Aceite</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <div className={`p-3.5 rounded-xl border ${contentBg} ${borderColor}`}>
                <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${textColorTertiary}`}>
                  Status do Contrato
                </span>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse flex-shrink-0"></span>
                  <span className="text-xs sm:text-sm font-bold text-green-600 dark:text-green-400">
                    {localApp.acceptedTerms || localApp.legalAcceptance?.accepted ? 'Aceito Eletronicamente' : 'Aceito por Adesão'}
                  </span>
                </div>
                <p className={`text-[10px] mt-1 ${textColorTertiary}`}>Fidelidade: 12 meses (Multa 30%)</p>
              </div>

              <div className={`p-4 rounded-xl border ${contentBg} ${borderColor}`}>
                <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${textColorTertiary}`}>
                  Data e Hora do Aceite
                </span>
                <p className={`text-xs sm:text-sm font-semibold truncate ${textColor}`}>
                  {localApp.legalAcceptance?.acceptedAt 
                    ? new Date(localApp.legalAcceptance.acceptedAt).toLocaleString('pt-BR')
                    : (localApp.termsAcceptedAt?.toDate ? localApp.termsAcceptedAt.toDate().toLocaleString('pt-BR') : localApp.createdAt?.toLocaleString('pt-BR') || 'Data do pedido')}
                </p>
                <p className={`text-[10px] mt-1 ${textColorTertiary}`}>Carimbo temporal gravado</p>
              </div>

              <div className={`p-4 rounded-xl border ${contentBg} ${borderColor}`}>
                <span className={`block text-[10px] font-bold uppercase tracking-widest mb-1 ${textColorTertiary}`}>
                  Identificador / Hash do Aceite
                </span>
                <p className="font-mono text-xs text-blue-600 dark:text-blue-400 truncate font-semibold" title={localApp.legalAcceptance?.documentHash || `ACEITE-${localApp.id}`}>
                  {localApp.legalAcceptance?.documentHash || `ACEITE-${localApp.id?.substring(0, 8).toUpperCase()}`}
                </p>
                <p className={`text-[10px] mt-1 truncate ${textColorTertiary}`}>IP: {localApp.legalAcceptance?.ipAddress || 'Web Client'}</p>
              </div>
            </div>
          </div>

          {/* Configurações Técnicas e Datas */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Configurações Técnicas */}
            <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={getIconBgClass()}>
                  <MdOutlineSettings className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${textColor}`}>Especificações Técnicas</h4>
                  <p className={`text-xs ${textColorSecondary}`}>Recursos e plataformas</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Plataformas</label>
                  <div className={`flex flex-wrap gap-2 p-3.5 rounded-xl border ${contentBg} ${borderColor}`}>
                    {localApp.platforms?.map((platform, index) => (
                      <span key={index} className={getPlatformBadgeClass()}>
                        {platform === 'android' ? 'Android' :
                          platform === 'ios' ? 'iOS' :
                            platform === 'web' ? 'Web' : platform}
                      </span>
                    )) || 'Não definido'}
                  </div>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Categoria</label>
                  <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                    {localApp.category === 'loja-virtual' ? 'Loja Virtual' :
                      localApp.category === 'delivery' ? 'Delivery' :
                        localApp.category === 'sistema-agendamento' ? 'Sistema de Agendamento' :
                          localApp.category === 'catalogo' ? 'Catálogo' :
                            localApp.category === 'outro' ? 'Outro' :
                              localApp.category || 'Não definida'}
                  </p>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Menu Customizado</label>
                  <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                    {localApp.hasMenu ? 'Habilitado' : 'Desabilitado'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Cor do Loader</label>
                    <div className={`flex items-center gap-3 p-3.5 rounded-xl border ${contentBg} ${borderColor}`}>
                      <div
                        className="w-5 h-5 rounded-full border border-gray-300 shadow-sm"
                        style={{ backgroundColor: localApp.loaderColor || '#3B82F6' }}
                      ></div>
                      <span className={`text-xs font-mono font-medium ${textColorTertiary}`}>{localApp.loaderColor || '#3B82F6'}</span>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Cor do Menu</label>
                    <div className={`flex items-center gap-3 p-3.5 rounded-xl border ${contentBg} ${borderColor}`}>
                      <div
                        className="w-5 h-5 rounded-full border border-gray-300 shadow-sm"
                        style={{ backgroundColor: localApp.menuColor || '#3B82F6' }}
                      ></div>
                      <span className={`text-xs font-mono font-medium ${textColorTertiary}`}>{localApp.menuColor || '#3B82F6'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Datas */}
            <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={getIconBgClass()}>
                  <MdOutlineCalendarToday className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${textColor}`}>Histórico</h4>
                  <p className={`text-xs ${textColorSecondary}`}>Datas importantes</p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Criado em</label>
                  <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                    {localApp.createdAt?.toLocaleString('pt-BR') || 'Data não disponível'}
                  </p>
                </div>

                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Atualizado em</label>
                  <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                    {localApp.updatedAt?.toLocaleString('pt-BR') || 'Data não disponível'}
                  </p>
                </div>

                {localApp.publishedAt && (
                  <div>
                    <label className={`block text-[10px] font-bold uppercase tracking-widest mb-1.5 ${textColorTertiary}`}>Publicado em</label>
                    <p className={`font-semibold p-3.5 rounded-xl border text-sm ${contentBg} ${borderColor} ${textColor}`}>
                      {localApp.publishedAt.toDate ? localApp.publishedAt.toDate().toLocaleString('pt-BR') :
                        localApp.publishedAt.seconds ? new Date(localApp.publishedAt.seconds * 1000).toLocaleString('pt-BR') :
                          'Data não disponível'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Imagens */}
          <div className={`p-6 rounded-2xl border ${sectionBg} ${borderColor}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className={getIconBgClass()}>
                <MdOutlineImage className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              </div>
              <div>
                <h4 className={`text-lg font-bold ${textColor}`}>Imagens e Ícones</h4>
                <p className={`text-xs ${textColorSecondary}`}>Recursos visuais do aplicativo</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Logo */}
              <div>
                <label className={`block text-[10px] font-bold uppercase tracking-widest mb-2 ${textColorTertiary}`}>Logo</label>
                {localApp.logo ? (
                  <div className={`p-4 rounded-xl border flex flex-col items-center justify-center ${contentBg} ${borderColor}`}>
                    <img
                      src={localApp.logo}
                      alt="Logo do app"
                      className="h-20 w-20 object-contain rounded-xl shadow-sm bg-white p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className={`hidden text-[10px] mt-2 text-center ${textColorTertiary}`}>Imagem indisponível</div>
                    <a href={localApp.logo} target="_blank" rel="noopener noreferrer" className={`text-[10px] font-semibold hover:underline mt-3 block text-center transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                      }`}>
                      Ver imagem
                    </a>
                  </div>
                ) : (
                  <div className={`p-6 rounded-xl border flex items-center justify-center h-full ${contentBg} ${borderColor}`}>
                    <span className={`text-xs font-medium ${textColorTertiary}`}>Sem logo</span>
                  </div>
                )}
              </div>

              {/* Splash Screen */}
              <div>
                <label className={`block text-[10px] font-bold uppercase tracking-widest mb-2 ${textColorTertiary}`}>Splash Screen</label>
                {localApp.splash ? (
                  <div className={`p-4 rounded-xl border flex flex-col items-center justify-center ${contentBg} ${borderColor}`}>
                    <img
                      src={localApp.splash}
                      alt="Splash screen do app"
                      className="h-20 w-20 object-contain rounded-xl shadow-sm bg-white p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className={`hidden text-[10px] mt-2 text-center ${textColorTertiary}`}>Imagem indisponível</div>
                    <a href={localApp.splash} target="_blank" rel="noopener noreferrer" className={`text-[10px] font-semibold hover:underline mt-3 block text-center transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                      }`}>
                      Ver imagem
                    </a>
                  </div>
                ) : (
                  <div className={`p-6 rounded-xl border flex items-center justify-center h-full ${contentBg} ${borderColor}`}>
                    <span className={`text-xs font-medium ${textColorTertiary}`}>Sem splash</span>
                  </div>
                )}
              </div>

              {/* Ícones */}
              <div>
                <label className={`block text-[10px] font-bold uppercase tracking-widest mb-2 ${textColorTertiary}`}>Ícones do Menu</label>
                {localApp.icons && localApp.icons.length > 0 ? (
                  <div className={`p-4 rounded-xl border flex flex-col items-center justify-center h-full ${contentBg} ${borderColor}`}>
                    <div className="flex flex-wrap justify-center gap-3 mb-3">
                      {localApp.icons.slice(0, 5).map((iconObj, index) => {
                        const iconDef = predefinedIcons.find(pi => pi.id === iconObj.id);
                        const IconComponent = iconDef ? iconDef.icon : null;
                        return (
                          <div key={index} className="flex justify-center items-center h-10 w-10 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            {IconComponent ? (
                              <IconComponent className={`w-5 h-5 ${textColorTertiary}`} />
                            ) : iconObj.url ? (
                              <img
                                src={iconObj.url}
                                alt={iconObj.name}
                                className="h-6 w-6 object-contain"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-widest text-center block ${textColorTertiary}`}>
                      {localApp.icons.length} {localApp.icons.length === 1 ? 'ícone' : 'ícones'}
                    </span>
                  </div>
                ) : (
                  <div className={`p-6 rounded-xl border flex items-center justify-center h-full ${contentBg} ${borderColor}`}>
                    <span className={`text-xs font-medium ${textColorTertiary}`}>Sem ícones</span>
                  </div>
                )}
              </div>
            </div>

            {/* URLs das imagens */}
            <div className={`mt-6 p-4 rounded-xl border ${contentBg} ${borderColor}`}>
              <h5 className={`font-bold text-xs uppercase tracking-widest mb-3 ${textColor}`}>URLs de Armazenamento</h5>
              <div className="space-y-3 text-xs">
                {localApp.logo && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                    <span className={`font-bold uppercase tracking-widest text-[10px] ${textColorTertiary}`}>Logo:</span>
                    <span
                      className={`cursor-pointer hover:underline break-all font-mono py-1 px-2 rounded-md bg-gray-100 dark:bg-gray-800 transition-colors ${isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`}
                      onClick={() => {
                        navigator.clipboard.writeText(localApp.logo);
                        alert('URL da logo copiada!');
                      }}
                      title="Clique para copiar"
                    >
                      {localApp.logo}
                    </span>
                  </div>
                )}
                {localApp.splash && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                    <span className={`font-bold uppercase tracking-widest text-[10px] ${textColorTertiary}`}>Splash:</span>
                    <span
                      className={`cursor-pointer hover:underline break-all font-mono py-1 px-2 rounded-md bg-gray-100 dark:bg-gray-800 transition-colors ${isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`}
                      onClick={() => {
                        navigator.clipboard.writeText(localApp.splash);
                        alert('URL da splash copiada!');
                      }}
                      title="Clique para copiar"
                    >
                      {localApp.splash}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé com Ações */}
        <div className={`px-8 py-5 border-t rounded-b-2xl ${footerBg} ${borderColor}`}>
          <div className="flex flex-col sm:flex-row gap-3 justify-end items-center">
            <button
              onClick={() => setLegalModalOpen(true)}
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-xl shadow-md hover:shadow-lg hover:bg-blue-700 transition-all flex items-center gap-2 justify-center text-sm font-bold"
            >
              <FiPrinter className="w-4 h-4" />
              Imprimir Aceite (PDF)
            </button>

            <button
              onClick={() => {
                setSelectedUser(allUsers.find(u => u.id === localApp.userId));
                setAlertModalOpen(true);
              }}
              className="w-full sm:w-auto px-6 py-2.5 bg-yellow-500 text-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 hover:bg-yellow-600 transition-all flex items-center gap-2 justify-center text-sm font-bold"
            >
              <MdMailOutline className="w-5 h-5" />
              Enviar Alerta ao Cliente
            </button>

            <button
              onClick={() => setSelectedApp(null)}
              className={`w-full sm:w-auto px-6 py-2.5 rounded-xl transition-all shadow-sm flex items-center gap-2 justify-center text-sm font-bold ${isDarkMode
                  ? 'bg-gray-800 border border-gray-700 text-white hover:bg-gray-700 hover:shadow-md'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:shadow-md'
                }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Fechar Detalhes
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Comprovante Jurídico Imprimível */}
      <LegalAcceptanceModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        app={localApp}
        user={allUsers?.find(u => u.uid === localApp.userId || u.id === localApp.userId || u.email === localApp.userEmail)}
      />
    </div>
  );
};

export default AppDetailsModal;